import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, Search, TrendingUp } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NewsletterSection from '../components/NewsletterSection';
import ResourcesPromoSection from '../components/ResourcesPromoSection';
import { ScrollReveal } from '../components/ScrollAnimations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticles();
    fetchCategories();
  }, [selectedCategory]);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      params.append('limit', '20');
      
      const response = await fetch(`${API_URL}/api/blog/articles?${params}`);
      const data = await response.json();
      setArticles(data.articles || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/categories`);
      const data = await response.json();
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const filteredArticles = articles.filter(article =>
    article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const featuredArticle = filteredArticles.find(a => a.featured);
  const regularArticles = filteredArticles.filter(a => !a.featured || a !== featuredArticle);

  return (
    <div className="bg-black min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-accent/5 to-transparent" />
        
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative">
          <ScrollReveal animation="fadeUp">
            <div className="text-center max-w-3xl mx-auto">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-white/60 text-sm mb-6">
                <TrendingUp size={16} className="text-accent" />
                Centre de ressources
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                Blog <span className="text-accent">SEO</span>
              </h1>
              <p className="text-white/60 text-lg">
                Conseils, guides et actualités pour optimiser votre présence en ligne 
                et améliorer votre référencement naturel.
              </p>
            </div>
          </ScrollReveal>

          {/* Search Bar */}
          <ScrollReveal animation="fadeUp" delay={200}>
            <div className="max-w-xl mx-auto mt-10">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={20} />
                <input
                  type="text"
                  placeholder="Rechercher un article..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-none px-12 py-4 text-white placeholder:text-white/40 focus:outline-none focus:border-accent transition-colors"
                />
              </div>
            </div>
          </ScrollReveal>

          {/* Categories */}
          <ScrollReveal animation="fadeUp" delay={300}>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 text-sm transition-all duration-300 ${
                  !selectedCategory 
                    ? 'bg-accent text-black' 
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
              >
                Tous
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => setSelectedCategory(cat.name)}
                  className={`px-4 py-2 text-sm transition-all duration-300 ${
                    selectedCategory === cat.name 
                      ? 'bg-accent text-black' 
                      : 'bg-white/5 text-white/60 hover:bg-white/10'
                  }`}
                >
                  {cat.name} ({cat.count})
                </button>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="w-12 h-12 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-white/60 text-lg">Aucun article trouvé</p>
              <p className="text-white/40 mt-2">Essayez une autre recherche ou catégorie</p>
            </div>
          ) : (
            <>
              {/* Featured Article */}
              {featuredArticle && (
                <ScrollReveal animation="fadeUp">
                  <Link 
                    to={`/blog/${featuredArticle.slug}`}
                    className="group block mb-12"
                  >
                    <div className="grid lg:grid-cols-2 gap-8 bg-white/[0.02] border border-white/10 p-6 lg:p-8 hover:border-accent/30 transition-colors">
                      <div className="aspect-video bg-gradient-to-br from-accent/20 to-purple-500/20 relative overflow-hidden">
                        {featuredArticle.image ? (
                          <img 
                            src={featuredArticle.image} 
                            alt={featuredArticle.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-6xl font-bold text-white/10">SEO</span>
                          </div>
                        )}
                        <div className="absolute top-4 left-4 px-3 py-1 bg-accent text-black text-xs font-medium">
                          À la une
                        </div>
                      </div>
                      <div className="flex flex-col justify-center">
                        <span className="text-accent text-sm font-medium mb-3">{featuredArticle.category}</span>
                        <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4 group-hover:text-accent transition-colors">
                          {featuredArticle.title}
                        </h2>
                        <p className="text-white/60 mb-6 line-clamp-3">{featuredArticle.excerpt}</p>
                        <div className="flex items-center gap-6 text-white/40 text-sm">
                          <span className="flex items-center gap-2">
                            <Calendar size={14} />
                            {new Date(featuredArticle.published_at).toLocaleDateString('fr-FR')}
                          </span>
                          <span className="flex items-center gap-2">
                            <Clock size={14} />
                            {featuredArticle.read_time} min de lecture
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </ScrollReveal>
              )}

              {/* Regular Articles Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {regularArticles.map((article, index) => (
                  <ScrollReveal key={article.id} animation="fadeUp" delay={index * 100}>
                    <Link 
                      to={`/blog/${article.slug}`}
                      className="group block h-full"
                    >
                      <article className="h-full bg-white/[0.02] border border-white/10 hover:border-accent/30 transition-all duration-300">
                        <div className="aspect-video bg-gradient-to-br from-accent/10 to-purple-500/10 relative overflow-hidden">
                          {article.image ? (
                            <img 
                              src={article.image} 
                              alt={article.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-4xl font-bold text-white/10">SEO</span>
                            </div>
                          )}
                        </div>
                        <div className="p-6">
                          <span className="text-accent text-xs font-medium">{article.category}</span>
                          <h3 className="text-lg font-semibold text-white mt-2 mb-3 group-hover:text-accent transition-colors line-clamp-2">
                            {article.title}
                          </h3>
                          <p className="text-white/50 text-sm line-clamp-2 mb-4">{article.excerpt}</p>
                          <div className="flex items-center justify-between text-white/40 text-xs">
                            <span className="flex items-center gap-1">
                              <Calendar size={12} />
                              {new Date(article.published_at).toLocaleDateString('fr-FR')}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={12} />
                              {article.read_time} min
                            </span>
                          </div>
                        </div>
                      </article>
                    </Link>
                  </ScrollReveal>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Resources Promo Banner */}
      <ResourcesPromoSection variant="banner" />

      {/* Newsletter Section */}
      <NewsletterSection />

      <Footer />
    </div>
  );
};

export default BlogPage;
